import { type Accessor, createSignal, getOwner, onCleanup, untrack } from "solid-js";

/**
 * Global active dragging item identifier.
 */
const [activeDragId, setActiveDragId] = createSignal<string | null>(null);

export function useActiveDragId(): Accessor<string | null> {
  return activeDragId;
}

export interface SortableActivators {
  draggable: "true";
  onDragStart: (e: DragEvent) => void;
  onDragEnd: (e: DragEvent) => void;
}

export interface SortableItem {
  ref: (el: HTMLElement) => void;
  dragActivators: SortableActivators;
  readonly isActiveDraggable: boolean;
}

export interface SortableOptions {
  onDrop?: (sourceId: string, targetId: string) => void;
}

/**
 * Native HTML5 sortable helper for SolidJS.
 * Replaces `@thisbeyond/solid-dnd` with zero dependencies and native browser drag & drop.
 */
export function createSortable(
  idOrGetter: string | Accessor<string>,
  options?: SortableOptions,
): SortableItem {
  const getId = typeof idOrGetter === "function" ? idOrGetter : () => idOrGetter;
  let elementRef: HTMLElement | null = null;

  const handleDragStart = (e: DragEvent) => {
    const id = getId();
    setActiveDragId(id);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", id);
      if (elementRef) {
        e.dataTransfer.setDragImage(elementRef, 20, 20);
      }
    }
  };

  const handleDragEnd = (_e: DragEvent) => {
    setActiveDragId(null);
  };

  const handleDragOver = (e: DragEvent) => {
    if (activeDragId()) {
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "move";
      }
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    const targetId = getId();
    const sourceId = e.dataTransfer?.getData("text/plain") || activeDragId();
    setActiveDragId(null);
    if (sourceId && sourceId !== targetId) {
      options?.onDrop?.(sourceId, targetId);
    }
  };

  if (getOwner()) {
    onCleanup(() => {
      if (elementRef) {
        elementRef.removeEventListener("dragover", handleDragOver);
        elementRef.removeEventListener("drop", handleDrop);
        elementRef = null;
      }
      if (untrack(activeDragId) === getId()) {
        setActiveDragId(null);
      }
    });
  }

  const ref = (el: HTMLElement) => {
    if (elementRef && elementRef !== el) {
      elementRef.removeEventListener("dragover", handleDragOver);
      elementRef.removeEventListener("drop", handleDrop);
    }
    elementRef = el;
    el.addEventListener("dragover", handleDragOver);
    el.addEventListener("drop", handleDrop);
  };

  return {
    ref,
    dragActivators: {
      draggable: "true",
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
    },
    get isActiveDraggable() {
      return activeDragId() === getId();
    },
  };
}
